import { catchError, map, Observable, of, tap, switchMap, startWith } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { ModuleDTO } from '../../../../dto/module/module.dto';
import { Module } from '../../../../models/module/Module.model';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { FunctionRegistryService } from '../../../function-registry/function-registry.service';
import { environment } from '../../../../environment/environment';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';

export function modules(componentDataSourceService: ComponentDataSourceService, functionRegistryService: FunctionRegistryService) {
  // Get the data sharing service from the component data source service
  const dataSharingService = componentDataSourceService.injector.get(DataSharingService);
  return {
    name: 'Modules',
    description: 'Lists all modules',
    execute: (params?: LoadDataParams) => {
      
      // Use reactive data sharing - this will automatically update when modules are edited
      return dataSharingService.getData$('modules_updated').pipe(
        startWith(null), // Start with null to trigger initial load
        switchMap(() => {
          return componentDataSourceService.http.get<any>(
            `${environment.apiUrl}/events/modules`,
            params?.queryParams ? { params: params.queryParams } : {}
          );
        }),
        tap(result => {
          if (result?.items) {
            if (result.items[0]?.versions) {
            }
          }
        }),
        catchError(error => {
          console.error('[MODULE] Error fetching modules:', error);
          if (error.error) {
            console.error('[MODULE] Error response body:', error.error);
          }
          return of({ items: [], totalCount: 0 });
        }),
        map(result => {
          if (result && result.items) {
            try {
              const modules = result.items.map((module: any) => {
                if (module.versions && module.versions.length > 0) {
                  
                  // Direct test with module.versions[0]
                  const testVersion = module.versions[0];
                  if (testVersion.descriptions && testVersion.descriptions['da']) {
                  } else {
                    // Check if the structure is different
                    if (testVersion.descriptions && Object.keys(testVersion.descriptions).length > 0) {
                      const firstKey = Object.keys(testVersion.descriptions)[0];
                    }
                  }
                }
                
                const moduleDTO: ModuleDTO = {
                  module_id: module.module_id,
                  base_module_id: module.base_module_id,
                  name: module.name,
                  descriptions: module.descriptions,
                  visibility: module.visibility,
                  subtypes: module.subtypes || [],
                  maintenance_minutes: module.maintenance_minutes || 0,
                  versions: module.versions || []
                };

                try {
                  const convertedModule = Module.fromDTO(moduleDTO);
                  
                  // For display purposes, get latest version info if available
                  let displayStartDate = null;
                  let displayEndDate = null;
                  let latestVersionDescription = '';
                  let latestVersionMaintenanceMinutes = convertedModule.maintenance_minutes || 0;
                  
                  if (convertedModule.versions && convertedModule.versions.length > 0) {
                    
                    // Sort versions by valid_from date (descending) to get the latest one
                    const sortedVersions = [...convertedModule.versions].sort((a, b) => 
                      new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime()
                    );
                    
                    const latestVersion = sortedVersions[0];
                    displayStartDate = latestVersion.valid_from;
                    displayEndDate = latestVersion.valid_to;
                    
                    // Get maintenance_minutes from the latest version
                    latestVersionMaintenanceMinutes = latestVersion.maintenance_minutes || 0;
                    
                    // Get description from the latest version if available
                    
                    if (latestVersion.descriptions && latestVersion.descriptions['da']) {
                      latestVersionDescription = latestVersion.descriptions['da'].translation_text || '';
                    } else {
                      // Try a different approach if the structure might be different
                      if (latestVersion.descriptions) {
                        // Try to find any key that might contain the description
                        for (const key of Object.keys(latestVersion.descriptions)) {
                          if (typeof latestVersion.descriptions[key] === 'object' && latestVersion.descriptions[key].translation_text) {
                            latestVersionDescription = latestVersion.descriptions[key].translation_text;
                            break;
                          }
                        }
                      }
                    }
                  } else {
                  }
                  
                  const result = {
                    ...convertedModule,
                    descriptions: convertedModule.descriptions?.['da']?.translation_text || '',
                    latestVersionDescription,
                    displayStartDate,
                    displayEndDate,
                    versionCount: convertedModule.versions?.length || 0,
                    maintenance_minutes: latestVersionMaintenanceMinutes
                  };
                  return result;
                } catch (error) {
                  console.error('[MODULE] Error converting DTO to model:', error);
                  throw error;
                }
              });

              return {
                items: modules,
                totalCount: result.totalCount,
                pageSize: result.pageSize,
                pageIndex: result.pageIndex
              };
            } catch (error) {
              console.error('[MODULE] Error in mapping process:', error);
              return { items: [], totalCount: 0 };
            }
          } else {
            console.warn('[MODULE] No items found in response');
            throw new Error('No modules found');
          }
        })
      );
    },
    settings: {
      columns: [
        { key: 'name', header: 'Navn', type: 'text' },
        { key: 'displayStartDate', header: 'Seneste version fra', type: 'date' },
        { key: 'displayEndDate', header: 'Seneste version til', type: 'date' },
        { key: 'versionCount', header: 'Antal versioner', type: 'number' },
        { key: 'maintenance_minutes', header: 'Vedligeholdelse (min)', type: 'number' },
        { key: 'latestVersionDescription', header: 'Beskrivelse', type: 'text', transform: (value: string) => value?.substring(0, 40) + (value?.length > 40 ? '...' : '') },
        {
          key: 'actions',
          header: '',
          type: 'button',
          buttonText: 'Rediger',
          buttonColor: 'primary',
          variant: 'raised',
          action: (row: any) => {
            
            // Save the module data to the data sharing service
            dataSharingService.setData('selected_module', row);
            
            // The dynamic-table component will now automatically pick up the modal's data
            functionRegistryService.executeFunction('openModal', 'edit_module', { data: row });
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc'
    }
  }
} 